// Handles converting json data into immutable Records

import {merge} from 'lodash';
import {List, fromJS} from 'immutable';
import {
  migrateLiteral,
  fixDataTypeJson,
  removeMismatchOverrides,
} from '@packages/systems/core/component-migrations';
import Field, {type FieldProps} from '@packages/systems/dynamo/records/Field';
import Site from '@packages/systems/core/records/Site';
import type {CodeComponentsSafeModeReason} from '@packages/domains/code-libraries/data-types';
import {cacheMemo, once, weakMemo} from '@packages/utilities/memo';
import {
  emptyArray,
  flatMap,
  identity,
  assoc,
  pipe,
  constant,
  constantIdentity,
  emptyObject,
} from '@packages/utilities/fp/utils';
import {
  everywhere as everywhereOnJson,
  parse as parseJson,
  type Json,
} from '@packages/utilities/json';
import type {JSONNode} from '@packages/shared/types/JSONNode';
import {migrateJsonNodeFromTagToType} from '@packages/systems/core/utils/TagToTypeMigrationUtils';
import {migrationJsonNodeAnimationToUnionType} from '@packages/systems/core/utils/LottieUtils';
import {migrateJsonNodeVideoData} from '@packages/systems/core/utils/VideoMigrationUtils';
import {reifyElementTypeInExpressionJson} from './reifyElementTypeInExpressionJson';
import {migrateRemoveTopLevelLayoutNone} from './migrateRemoveTopLevelLayoutNone';
import {migrateOverridesToData} from './migrateOverridesToData';
import {migrateReferenceToVariableAndSelect} from './migrateReferenceToVariableAndSelect';
import {migrateFlattenOverrides} from './migrateFlattenOverrides';
import {
  typeName,
  iconName,
  namesEqual,
  qualify,
  QualifiedMap,
  MutableQualifiedMap,
  Qualified,
  getName,
} from '@packages/systems/wfdl-Qualified';
import {
  type Type,
  Component,
  matchExpression,
  matchType,
  Plugins,
  Types,
  Expressions,
  TypeImplementation,
  parseExpression,
} from '@packages/systems/wfdl';
import {
  booleanTypeName,
  numberTypeName,
  textTypeName,
  enumTypeName,
  EElement,
  EText,
  ERecord,
  EBoolean,
  ENumber,
  EEnum,
  EUnion,
  EList,
  ELiteral,
  EDistinct,
  TRecord,
  TEmptyRecord,
  TListOfElement,
  TTypeReference,
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {reportBug, reportBugOnce} from '@packages/utilities/reporter';

import {rootIds, getElementTypeAndDataType} from './rootIds';
import {coerceIframeElement} from './coerceIframeElement';

export type SiteShape = {
  _id?: string;
  database: string | undefined;
  databaseVersion?: string;
  faviconUrl?: string;
  faviconDarkUrl?: string;
  faviconUrl48?: string;
  lastPublished?: string;
  name?: string;
  responsiveImages?: boolean;
  shortName?: string;
  sslHosting?: boolean;
  timezone?: string | null;
  webClipUrl?: string;
  webClipUrl180?: string;
  webClipUrl192?: string;
  recaptchaSiteKey?: string;
  recaptchaRequired?: boolean;
  intellimizeCustomerId?: string;
  intellimizeDataCollectionEnabled?: boolean;
  intellimizeDataCollectionType?: 'always' | 'optOut' | 'disabled';
  intellimizeAntiFlickerTimeout?: number | null;
  codeComponentsSafeModeReason?: CodeComponentsSafeModeReason | null;
};

export type SiteMeta = {
  base?: string;
  beautifyHtml?: boolean;
  postBody?: string;
  googleAnonymizeIP?: boolean;
  googleAnalyticsId?: string;
  googleVerification?: string;
  globalCanonicalTag?: string;
  includePaginationParamsInCanonical?: boolean;
  googleUniversalAnalyticsId?: string;
  googleAnalyticsFourId?: string;
  googleTagIds?: Array<{order: number; displayName: string; tagId: string}>;
  googleGtagEnabled?: boolean;
  googleOptimizeId?: string;
  facebookPixelId?: string;
  facebookPixelLazyLoad?: boolean;
  defaultSitemap?: boolean;
  head?: string;
  hideBranding?: boolean;
  lang?: string;
  ogDesc?: string;
  ogImages?: Array<string>;
  ogTitle?: string;
  seoDesc?: string;
  seoTitle?: string;
  showWebflowBadge?: boolean;
  siteBasePath?: string | null | undefined;
  site?: string;
  typeKitKitId?: string;
  useDefaultDomainForGlobalCanonical?: boolean;
};

/** An index for mapping json nodes. Helper for a more accessible node map. */
type JSONNodeIndex = {[key: string]: JSONNode};

/**
 * Prunes dismissedPropElementIds to only include IDs for elements that
 * still exist in the JSON node index to determine if we should keep the dismissedPropElementIds.
 */
const pruneDismissedPropElementIds = (
  rawDismissedIds: string[] | undefined,
  index: JSONNodeIndex
): string[] | undefined => {
  if (!rawDismissedIds || rawDismissedIds.length === 0) {
    return undefined;
  }

  const validDismissedIds = rawDismissedIds.filter((id) => index[id]);

  return validDismissedIds.length > 0 ? validDismissedIds : undefined;
};

const componentIcon = once(() =>
  qualify(Plugins.name('Basic'), iconName('Symbol'))
);

// Accepts `data.sym.defaultData` as either a JSON-stringified expression or a
// non-stringified expression.
export const parseDefaultData = (
  rawDefaultData: Json
): Expression | undefined => {
  const json =
    typeof rawDefaultData === 'string'
      ? parseJson(rawDefaultData)
      : rawDefaultData;
  if (json == null) return undefined;
  const parsedExpression = parseExpression(json);
  return parsedExpression.type === 'WellformedExpression'
    ? parsedExpression.val
    : undefined;
};
const pageIcon = once(() => qualify(Plugins.name('Basic'), iconName('Page')));

const linkTypeName = once(() =>
  qualify(Plugins.name('Basic'), typeName('Link'))
);
const imageTypeName = once(() =>
  qualify(Plugins.name('Asset'), typeName('Image'))
);

export const reifySite = (
  site: SiteShape,
  meta: SiteMeta,
  cdnHost?: string,
  sslOk?: boolean
) =>
  Site({
    id: site._id,
    base: meta.base,
    beautifyHtml: meta.beautifyHtml,
    bodyHtml: meta.postBody || '',
    database: site.database,
    databaseVersion: site.databaseVersion,
    faviconUrl: site.faviconUrl,
    googleAnonymizeIP: meta.googleAnonymizeIP,
    googleAnalyticsId: meta.googleAnalyticsId,
    googleUniversalAnalyticsId: meta.googleUniversalAnalyticsId,
    googleAnalyticsFourId: meta.googleAnalyticsFourId,
    googleVerification: meta.googleVerification,
    globalCanonicalTag: meta.globalCanonicalTag,
    facebookPixelId: meta.facebookPixelId,
    facebookPixelLazyLoad: meta.facebookPixelLazyLoad,
    headHtml: meta.head || '',
    hideBranding: meta.hideBranding,
    lang: meta.lang,
    lastPublished: site.lastPublished,
    name: site.name || '',
    ogDesc: meta.ogDesc || '',
    // @ts-expect-error - TS2769 - No overload matches this call.
    ogImages: List(meta.ogImages),
    ogTitle: meta.ogTitle || '',
    responsiveImages: site.responsiveImages,
    seoDesc: meta.seoDesc || '',
    seoTitle: meta.seoTitle || '',
    shortName: site.shortName || '',
    showWebflowBadge: meta.showWebflowBadge,
    pageLinkHrefPrefix: meta.siteBasePath || '',
    siteId: meta.site,
    sslHosting: site.sslHosting,
    timezone: site.timezone || 'UTC',
    typeKitKitId: meta.typeKitKitId,
    webClipUrl: site.webClipUrl,
    cdnHost,
    sslOk: Boolean(sslOk),
    recaptchaSiteKey: site.recaptchaSiteKey,
    intellimizeDataCollectionEnabled: site.intellimizeDataCollectionEnabled,
    intellimizeDataCollectionType: site.intellimizeDataCollectionType,
    intellimizeAntiFlickerTimeout: site.intellimizeAntiFlickerTimeout,
    codeComponentsSafeModeReason: site.codeComponentsSafeModeReason,
  });

/**
 * Input type for reifyField - accepts plain JS objects that will be converted to Field records.
 * Defined in terms of what reifyField actually uses, with validations allowing any input
 * since fromJS() handles the conversion.
 */
export type ReifyFieldInput = Partial<Omit<FieldProps, 'validations'>> & {
  validations?: any;
};

export const reifyField = (json: ReifyFieldInput) => {
  return Field({
    id: json.id,
    editable: json.editable || false,
    name: json.name,
    required: json.required || false,
    slug: json.slug,
    type: json.type,
    helpText: json.helpText || '',
    validations: fromJS(json.validations || {}) as any,
    innerType: json.innerType,
  });
};

const patchExpression = everywhereOnJson(
  pipe([
    reifyElementTypeInExpressionJson,
    migrateOverridesToData,
    migrateReferenceToVariableAndSelect,
  ])
);

// After removing `instDataType` if elements ended up having `Typed`
// for example added by stale multiplayer client, `Typed` expressions would end
// up under `bind`. Here we remove that layer so that the node can be imported.
const removeTyped = Expressions.everywhere(
  matchExpression({
    Typed: ({expr}) => expr,
    _: identity,
  })
);

const setBind = assoc('bind');

const getDataTypeForReification = (jsonNode: JSONNode, dataType?: Type) => {
  const shouldFallbackToLegacyReificationBehavior =
    (!dataType || Plugins.isDefaultDataType(dataType)) &&
    Array.isArray(jsonNode.children);

  /*
   * We have children provided, but the data type doesn't declare
   * children. This is to support nodes that have children but don't declare
   * that in their type or nodes that have corrupt children, without it,
   * data would be deleted or corrupted. Or the runtime will crash.
   */
  if (shouldFallbackToLegacyReificationBehavior) {
    return TRecord(
      {
        children: TListOfElement,
      },
      TTypeReference('extraAtomFields')
    );
  }

  return dataType;
};

const isValidElement = (childId: any, index: JSONNodeIndex) => {
  // Skip corrupt children!
  if (!(typeof childId === 'string' && index[childId])) {
    reportBugOnce(
      'InvalidElementRemoved',
      'Reification skipped this element because it is corrupt or missing from the index.',
      {
        element: childId,
        team: 'Platform',
        ticket: 'https://webflow.atlassian.net/browse/PLAT-1477',
      }
    );
    return false;
  }
  return true;
};

// Reify a JSONNode to an Expression Element. DataTypes are needed in order to
// build up the Element data in the correct shape.
const reifyExpressionElement = ({
  jsonNode,
  dataTypes,
  plugins,
  index,
}: {
  jsonNode: JSONNode;
  dataTypes: Plugins.DataTypes;
  plugins: Plugins.PluginsType;
  index: JSONNodeIndex;
}): ExpressionElement => {
  // If json.text == true we have a type three text node and can early return a
  // String element. Fast path — skip migrations and defaults since they're
  // all no-ops. Text nodes are the substantial majority of nodes.
  if (jsonNode.text) {
    const textEl: ExpressionElement = {
      id: jsonNode._id,
      type: qualify(Plugins.name('Basic'), Component.name('String')),
      data: EText(jsonNode.v || jsonNode.data?.value || ''),
    };
    if (jsonNode.meta) textEl.meta = jsonNode.meta;
    return textEl;
  }

  let json: JSONNode = migrateJsonNodeFromTagToType(jsonNode);

  json = migrationJsonNodeAnimationToUnionType(json);
  json = migrateJsonNodeVideoData(json);
  json = coerceIframeElement(json);
  // Only run patchExpression if the node has a bind property — most don't.
  if (json.bind) {
    json = setBind(patchExpression(removeTyped(json.bind)))(json);
  }

  const data: Record<string, any> = {};
  const jsonData = json.data;
  if (jsonData) {
    for (const k in jsonData) {
      data[k] = jsonData[k];
    }
  }
  if (json.classes && json.classes.length) data.styleBlockIds = json.classes;
  if (json.v) data.value = json.v;

  const meta = json.meta || null;

  const inst = data.sym?.inst;
  const [elementType, dataType] = getElementTypeAndDataType(
    plugins,
    dataTypes,
    json.type || '',
    inst
  );

  // Reparent json.children within data.
  // Filter out corrupt children — a valid child is a string ID that exists
  // in the node index.
  // Fast path: assign directly if all children are valid (common case).
  if (json.children?.length) {
    const children = json.children;
    let hasInvalidChild = false;
    // First pass: check if any child is missing from the index.
    for (let i = 0; i < children.length; i++) {
      const childId = children[i];
      if (!(typeof childId === 'string' && index[childId])) {
        hasInvalidChild = true;
        break;
      }
    }
    if (hasInvalidChild) {
      // Second pass: build a new array with only indexed children.
      const validChildren = [];
      for (let i = 0; i < children.length; i++) {
        const childId = children[i];
        if (typeof childId === 'string' && index[childId]) {
          validChildren.push(childId);
        }
      }
      data.children = validChildren;
    } else {
      data.children = children;
    }
  } else if (json.children && json.children.length === 0 && !inst && dataType) {
    // Check if type declares children (inlined from IIFE)
    const topLevelFields = Types.getRecordFields(dataType);
    if (topLevelFields && topLevelFields.children) {
      // Special case that makes sure that if a non-component instance has empty
      // children, and has children dataType that we keep that empty array as children.
      // If for some reason we have children under data, keep those.
      data.children = data.children ? data.children : emptyArray;
    }
  }

  // If the json node includes sym.inst information we are dealing with a
  // Component Instance and need to do the following special methods:
  //   - Call `fixDataTypeJson` to amend any missing pieces in the instDataType
  //     and convert potential NodePojos to Elements
  //   - For sym.overrides we need to `reifyToExpressions` for those that are of type
  //     `List<Element>` and rebuild what data should live under data
  if (inst) {
    const rawInstDataType = data.sym.instDataType;
    let instanceData: Record<string, any> = {};
    if (rawInstDataType) {
      // We store Types as strings in Mongo to work around nesting limit.
      // Use a bounded LRU cache for parsed+fixed instDataTypes by string.
      // Instances of the same component share identical instDataType strings.
      if (typeof rawInstDataType === 'string') {
        const fixed = parseAndFixDataType(rawInstDataType);
        data.sym = {...data.sym, instDataType: fixed};
      } else {
        const newInstDataType = fixDataTypeJson(rawInstDataType);
        if (newInstDataType !== rawInstDataType) {
          data.sym = {...data.sym, instDataType: newInstDataType};
        }
      }
    }
    const componentSymFields = data.sym.instDataType
      ? Types.getRecordFields(data.sym.instDataType)
      : null;
    const symType = componentSymFields?.sym;
    const parentSymInstFields = symType ? Types.getRecordFields(symType) : null;

    const symInstDataType = parentSymInstFields?.overrides;
    const instanceDataType = symInstDataType || dataType;

    const {sym: _sym, ...dataWithoutSym} = data;
    if (data.sym.overrides) {
      instanceData = {...dataWithoutSym, ...data.sym.overrides};
    } else {
      instanceData = dataWithoutSym;
    }

    const dataExpr = buildInstanceData(
      plugins,
      dataTypes,
      getDataTypeForReification(json, instanceDataType),
      instanceData,
      json.bind,
      index
    );
    const ensuredDataExpr = ensureTopLevelDataExpression(dataExpr);

    const patchedData =
      symInstDataType && dataType
        ? removeMismatchOverrides(symInstDataType, dataType, ensuredDataExpr)
        : ensuredDataExpr;

    const instResult: ExpressionElement = {
      id: json._id,
      type: elementType,
      data: patchedData,
    };
    if (meta) instResult.meta = meta;
    return instResult;
  }

  // Only remove sym key if it exists — most non-instance elements don't have it.
  let dataForBuild = data;
  if (data.sym !== undefined) {
    const {sym: _s, ...rest} = data;
    dataForBuild = rest;
  }
  const dataExpr = buildData(
    plugins,
    dataTypes,
    getDataTypeForReification(json, dataType),
    dataForBuild,
    json.bind,
    index
  );

  const result: ExpressionElement = {
    id: json._id,
    type: elementType,
    data: ensureTopLevelDataExpression(dataExpr),
  };
  if (meta) result.meta = meta;
  return result;
};

const reifyElement = (opts: {
  jsonNode: JSONNode;
  dataTypes: Plugins.DataTypes;
  plugins: Plugins.PluginsType;
  index: JSONNodeIndex;
}): Expression => EElement(reifyExpressionElement(opts));

// We don't want to expose `reifyElement` for general use, for tests we export
// this separate variable.
export const reifyElementForTest = reifyExpressionElement;

// Given any data, try to build up some Expression representation of that data.
const naiveDataToExpression = (data: any): Expression | undefined => {
  if (typeof data === 'string') return EText(data);
  if (typeof data === 'boolean') return EBoolean(data);
  if (typeof data === 'number') return ENumber(data);
  if (Array.isArray(data)) {
    // There are some ANS codepaths that introduce null and undefined
    // values. Make sure we ignore them.
    const expressions = [];
    for (let i = 0; i < data.length; i++) {
      if (data[i] != null) {
        const itemExpression = naiveDataToExpression(data[i]);
        if (itemExpression != null) expressions.push(itemExpression);
      }
    }
    return EList(expressions);
  }
  if (typeof data === 'object' && data !== null) {
    // There are some ANS codepaths that introduce null and undefined
    // values. Make sure we ignore them.
    const acc: Record<string, Expression> = {};
    for (const key in data) {
      const val = data[key];
      if (val != null) {
        const childExpression = naiveDataToExpression(val);
        if (childExpression != null) acc[key] = childExpression;
      }
    }
    return ERecord(acc);
  }

  return undefined;
};

const ensureTopLevelDataExpression = (
  expression: Expression | undefined
): Expression => expression ?? ERecord({});

// For symbol instances we don't apply default values.
const buildInstanceData = (
  plugins: Plugins.PluginsType,
  dataTypesById: Plugins.DataTypes,
  dataType: Type | undefined,
  data: {[key: string]: any},
  bind: Expression | undefined,
  index: JSONNodeIndex
): Expression | undefined => {
  const fields = dataType ? Types.getRecordFields(dataType) : null;
  if (fields) {
    // Special case: We're working with a `bind` that recurses down into
    // `sym.overrides`. This happens when binding a nested component instance
    // to something in context, like an override field of the wrapping
    // component.
    const baseRecordVal = bind ? getSymOverrides(bind) : {};
    const properties: Record<string, Expression> = {};
    for (const key in baseRecordVal) {
      const baseRecordValue = baseRecordVal[key];
      if (fields[key] && baseRecordValue) {
        properties[key] = baseRecordValue;
      }
    }
    for (const key in data) {
      const field = fields[key];
      if (!field) {
        continue;
      }

      const dataValue = data[key];
      const bindValue = properties[key];

      if (bindValue) {
        const bindValueObj = Expressions.getRecord(bindValue);

        // If `bindValueObj` exists, it means we have a Record property on `data`
        // that also exists on `bind`. We need to account for Records that have multiple
        // properties under the same key. Essentially merging the two rather than
        // preferring `bind` over `data`.
        if (bindValueObj) {
          const builtData = buildData(
            plugins,
            dataTypesById,
            field,
            dataValue,
            properties[dataValue],
            index
          );
          if (builtData == null) {
            continue;
          }
          // Distinct Expression value contains the data we want to merge
          const distinctExpr = Expressions.getDistinct(builtData);
          properties[key] = distinctExpr
            ? EDistinct({
                name: distinctExpr.name,
                value: ERecord(
                  merge(
                    {},
                    Expressions.getRecord(distinctExpr.value),
                    bindValueObj
                  )
                ),
              })
            : ERecord(
                merge({}, Expressions.getRecord(builtData), bindValueObj)
              );
        }

        continue;
      }

      if (dataValue != null) {
        const childExpression = buildData(
          plugins,
          dataTypesById,
          field,
          dataValue,
          properties[dataValue],
          index
        );
        if (childExpression != null) {
          properties[key] = childExpression;
        }
      }
    }

    return ERecord(properties);
  }

  return buildData(plugins, dataTypesById, dataType, data, bind, index);
};

const buildLiteralOrUniqueExpression = (
  plugins: Plugins.PluginsType,
  ty: Type,
  data: any,
  bind: Expression | undefined,
  dataTypes: Plugins.DataTypes,
  jsonNodeIndex: JSONNodeIndex
): Expression | undefined => {
  // Handle Custom and Unique types
  const deconstructedTyApp = Types.deconstructTypeApplication(ty);
  if (!deconstructedTyApp) {
    return naiveDataToExpression(data);
  }
  const name = deconstructedTyApp.con;
  const typeImpls = Plugins.getDataTypes(plugins);
  const typeImpl = typeImpls.get(name);

  // Unknown type
  if (!typeImpl) {
    return naiveDataToExpression(data);
  }

  return TypeImplementation.match({
    Custom: () => ELiteral({name, value: data}),
    Unique: () => {
      const innerType = Types.getUniqueType(ty, plugins);
      if (!innerType) {
        throw new Error(`Unique type ${Types.print(ty)} has no inner type.`);
      }
      const value = buildData(
        plugins,
        dataTypes,
        innerType,
        data,
        bind,
        jsonNodeIndex
      );
      if (value == null) {
        return undefined;
      }
      return EDistinct({
        name,
        value,
      });
    },
    // This should never happen as Aliases are already unwrapped before we
    // would ever get there
    Alias: () => naiveDataToExpression(data),
  })(typeImpl);
};

const buildDataWithType = matchType<
  (
    arg1: Expression | undefined,
    arg2: any,
    arg3: Plugins.PluginsType,
    arg4: Plugins.DataTypes,
    arg5: JSONNodeIndex
  ) => Expression | undefined
>({
  TypeReference: constant((bind, data) => bind || naiveDataToExpression(data)),
  Element: constant((bind, data, plugins, dataTypes, index) => {
    if (bind) return bind;
    const childNode = index[data];
    if (!childNode)
      throw new Error(`Child Node ${data} missing from flattened index!`);
    return reifyElement({
      jsonNode: childNode,
      dataTypes,
      plugins,
      index,
    });
  }),
  TypeConstructor:
    (name, ty) =>
    (
      bind: Expression | undefined,
      data: any,
      plugins: Plugins.PluginsType,
      dataTypes: Plugins.DataTypes,
      jsonNodeIndex: JSONNodeIndex
    ) => {
      if (bind) return migrateLiteral(bind);

      // Text type is a TypeConstructor but needs special handling to only create an EText
      if (namesEqual(name, textTypeName)) {
        return EText(data);
      }

      // Boolean type is a TypeConstructor but needs special handling to only create an EBoolean
      if (namesEqual(name, booleanTypeName)) {
        return EBoolean(data);
      }

      if (namesEqual(name, numberTypeName)) {
        return ENumber(data);
      }

      return buildLiteralOrUniqueExpression(
        plugins,
        ty,
        data,
        bind,
        dataTypes,
        jsonNodeIndex
      );
    },
  TypeApplication:
    ({con}, ty) =>
    (
      bind: Expression | undefined,
      data: any,
      plugins: Plugins.PluginsType,
      dataTypes: Plugins.DataTypes,
      jsonNodeIndex: JSONNodeIndex
    ) => {
      if (bind) return bind;
      const name = Types.getTypeConstructor(con);

      // Enum type is a TypeApplication but needs special handling to only create an EEnum
      if (name && namesEqual(name, enumTypeName)) {
        return typeof data === 'string'
          ? EEnum(data)
          : naiveDataToExpression(data);
      }

      // StyleVariant override data will be a string representing the ID of a selected variant
      // Return an ELiteral wrapping this value to correctly initialize StyleVariant fields with overrides
      if (Types.isStyleVariantType(con)) {
        const deconstructedName = Types.deconstructTypeApplication(con)?.con;

        return deconstructedName && typeof data === 'string'
          ? ELiteral({name: deconstructedName, value: data})
          : naiveDataToExpression(data);
      }

      return buildLiteralOrUniqueExpression(
        plugins,
        ty,
        data,
        bind,
        dataTypes,
        jsonNodeIndex
      );
    },
  Generic:
    ({body}) =>
    (
      bind: Expression | undefined,
      data: any,
      plugins: Plugins.PluginsType,
      dataTypesById: Plugins.DataTypes,
      index: JSONNodeIndex
    ) =>
      buildData(plugins, dataTypesById, body, data, bind, index),
  _: (ty) => () => {
    throw new Error(`Cannot build an expression for Type ${Types.print(ty)}`);
  },
});

// merges two records together, preferring the values in the incoming record
const mergeRecords = (
  current: {[key: string]: Expression},
  incoming: {[key: string]: Expression | undefined}
): {[key: string]: Expression} => {
  const merged = {...current};
  for (const key in incoming) {
    const mergedValue = merged[key];
    const incomingValue = incoming[key];
    if (
      mergedValue &&
      incomingValue &&
      Expressions.isRecord(mergedValue) &&
      Expressions.isRecord(incomingValue)
    ) {
      const currentRecord = Expressions.getRecord(mergedValue) || {};
      const incomingRecord = Expressions.getRecord(incomingValue) || {};
      merged[key] = ERecord(mergeRecords(currentRecord, incomingRecord));
    } else if (incomingValue != null) {
      merged[key] = incomingValue;
    }
  }
  return merged;
};

// Fixes data corruption caused by
// https://github.com/webflow/webflow/pull/37528 where undefined
// Link and Image types were defaulting to empty strings.
const isCorruptedEmptyField = (fieldType: Type): boolean => {
  const name = Types.getTypeConstructor(fieldType);
  return Boolean(
    name &&
    (namesEqual(name, imageTypeName()) || namesEqual(name, linkTypeName()))
  );
};

const buildData = (
  plugins: Plugins.PluginsType,
  dataTypesById: Plugins.DataTypes,
  dataType: Type | undefined,
  data: any,
  bind: Expression | undefined,
  index: JSONNodeIndex
): Expression | undefined => {
  if (!dataType) {
    if (bind) return bind;
    return naiveDataToExpression(data);
  }
  // Fast path: string data without bind — common for tag, class name fields.
  if (typeof data === 'string' && !bind) {
    const aliasType = Types.getAliasType(dataType, plugins);
    if (
      !aliasType &&
      !Types.getRecordFields(dataType) &&
      !Types.getUnionValues(dataType) &&
      !Types.getListItemType(dataType)
    ) {
      return buildDataWithType(dataType)(
        undefined,
        data,
        plugins,
        dataTypesById,
        index
      );
    }
  }
  if (data == null) {
    data = Types.getTypeDefaultValue(plugins)(dataType);

    if (Types.isListOfElements(dataType)) {
      data = reifyToExpressions(data, dataTypesById, plugins) as any;
    }
  }
  const aliasType = Types.getAliasType(dataType, plugins);
  if (aliasType) {
    return buildData(plugins, dataTypesById, aliasType, data, bind, index);
  }

  const fields = Types.getRecordFields(dataType);
  if (fields) {
    if (bind && Expressions.isBinding(bind)) return bind;

    if (!(typeof data === 'object' && data !== null))
      return naiveDataToExpression(data);

    const properties: {[key: string]: Expression} = {};

    for (const key in data) {
      const fieldType = fields[key];
      const rawVal = data[key];
      // There are some ANS codepaths that introduce null and undefined
      // values. In some cases, this is corrupted persisted data that needs
      // to be tolerated gracefully here.
      if (!fieldType && rawVal == null) continue;
      const val =
        rawVal === '' && fieldType
          ? isCorruptedEmptyField(fieldType)
            ? undefined
            : rawVal
          : rawVal;
      const childExpression = buildData(
        plugins,
        dataTypesById,
        fieldType,
        val,
        undefined,
        index
      );
      if (childExpression != null) {
        properties[key] = childExpression;
      }
    }

    for (const key in fields) {
      if (properties[key]) continue;
      const fieldType = fields[key];
      const rawVal = data[key];
      if (!fieldType && rawVal == null) continue;
      const val =
        rawVal === '' && fieldType
          ? isCorruptedEmptyField(fieldType)
            ? undefined
            : rawVal
          : rawVal;
      const childExpression = buildData(
        plugins,
        dataTypesById,
        fieldType,
        val,
        undefined,
        index
      );
      if (childExpression != null) {
        properties[key] = childExpression;
      }
    }

    if (bind) {
      // bind expressions take priority during reification
      const bindRecordVal = Expressions.getRecord(bind) || {};
      const merged = mergeRecords(properties, bindRecordVal);
      return ERecord(merged);
    }
    return ERecord(properties);
  }

  const unionValues = Types.getUnionValues(dataType);
  if (unionValues) {
    if (bind && Expressions.isBinding(bind)) return bind;

    if (bind) {
      const union = Expressions.getUnion(bind);

      if (!union) return naiveDataToExpression(data);

      const {tag, value} = union;

      if (typeof data !== 'object' || data.tag !== tag) return bind;

      const valType = unionValues[tag];
      const dataVal = data.val;
      const valueExpression = buildData(
        plugins,
        dataTypesById,
        valType,
        dataVal,
        value,
        index
      );
      if (valueExpression == null) {
        return bind;
      }

      return EUnion({
        tag,
        value: valueExpression,
      });
    }

    if (!(typeof data === 'object' && data !== null))
      return naiveDataToExpression(data);

    const tag = data.tag;
    const val = data.val;

    if (typeof tag !== 'string') {
      return naiveDataToExpression(data);
    }

    const valType = unionValues[tag];
    const valueExpression = buildData(
      plugins,
      dataTypesById,
      valType,
      val,
      undefined,
      index
    );
    if (valueExpression == null) {
      return undefined;
    }

    return EUnion({
      tag,
      value: valueExpression,
    });
  }

  const listType = Types.getListItemType(dataType);
  if (listType) {
    if (bind) return bind;
    if (!Array.isArray(data)) return naiveDataToExpression(data);

    const isElementExpected = Types.isElementType(listType);
    return EList(
      data.reduce<Array<any>>(function validateAndBuild(acc, item) {
        if (isElementExpected && !isValidElement(item, index)) {
          return acc;
        }
        const itemExpression = buildData(
          plugins,
          dataTypesById,
          listType,
          item,
          undefined,
          index
        );
        if (itemExpression != null) {
          acc.push(itemExpression);
        }
        return acc;
      }, [])
    );
  }

  return buildDataWithType(dataType)(bind, data, plugins, dataTypesById, index);
};

export const reifyComponentJSONNodesOnComponentMap = (
  componentMap: Component.ComponentMap,
  componentType: Qualified<Component.ComponentName>,
  rawComponentNodes: Array<JSONNode>,
  plugins: Plugins.PluginsType
): Component.ComponentMap => {
  const dataTypes = dataTypesFromComponents(plugins, componentMap);
  const componentNodes = migrateFlattenOverrides(
    plugins,
    dataTypes,
    rawComponentNodes
  );
  const componentNodesIndex = indexJsonDomNodeArrayById(componentNodes);

  const isPage = namesEqual(
    qualify(Plugins.name('__SitePlugin'), Component.name('page')),
    componentType
  );

  let node;
  if (isPage) {
    const pageRootIds = rootIds(plugins, dataTypes)(componentNodes);
    const bodyId = pageRootIds.find(
      (id) => componentNodesIndex[id]?.type === 'Body'
    );
    if (bodyId) {
      node = componentNodesIndex[bodyId];
    }
  } else {
    const componentId = Component.nameToString(getName(componentType));
    node = componentNodesIndex[componentId];
  }

  if (!node) {
    throw new Error(`Could not find root node for ${componentType}.`);
  }

  const component = reifyComponent(
    componentNodesIndex,
    node,
    isPage
      ? dataTypes
      : dataTypes.set(
          qualify(Plugins.name('__SitePlugin'), Component.name(node._id)),
          fixDataTypeJson(
            typeof node.dataType === 'string'
              ? JSON.parse(node.dataType)
              : node.dataType
          )
        ),
    plugins
  );

  // We assume that every other top level component that comes from
  // JSONNodes is a user component
  if (isPage) {
    return componentMap.set(
      componentType,
      Component.update(component, {
        displayName: 'Page',
        icon: pageIcon(),
      })
    );
  } else {
    return componentMap.set(componentType, component);
  }
};

const isSymbolRoot = (node: JSONNode) => node.data?.sym?.root;
const symbolRoots = weakMemo((symbols: Array<JSONNode>) =>
  symbols.filter(isSymbolRoot)
);

// Use a bounded LRU cache for dataType strings → parsed+fixed Type objects.
// Many components share identical dataType strings.
const parseAndFixDataType = cacheMemo(500)(
  (rawDataType: string): Type => fixDataTypeJson(JSON.parse(rawDataType))
);

export const dataTypesFromSymbolNodes = weakMemo(
  (plugins: Plugins.PluginsType) =>
    weakMemo((symbolNodes: Array<JSONNode>): Plugins.DataTypes => {
      const symbolDataTypes = symbolRoots(symbolNodes).reduce<
        Record<string, Type>
      >((acc, node) => {
        const rawDataType = node.dataType;
        if (!rawDataType) {
          acc[node._id] = TEmptyRecord;
          return acc;
        }

        // We store Types as strings in Mongo to work around nesting limit.
        if (typeof rawDataType === 'string') {
          const fixed = parseAndFixDataType(rawDataType);
          acc[node._id] = fixed;
          return acc;
        }

        acc[node._id] = fixDataTypeJson(rawDataType);
        return acc;
      }, {});

      return Plugins.getPluginDataTypes(plugins).union(
        new QualifiedMap({__SitePlugin: symbolDataTypes})
      );
    })
);

// Main method used during initial reification to build up a Component.ComponentMap.
export const reifyJSONNodesToComponentMap = (
  pageNodes: Array<JSONNode>,
  symbolNodes: Array<JSONNode>,
  plugins: Plugins.PluginsType,
  dataTypes: Plugins.DataTypes
): Component.ComponentMap => {
  const componentMap = new MutableQualifiedMap<
    Component.ComponentName,
    Component.ComponentType
  >();
  const sitePluginName = Plugins.name('__SitePlugin');

  // SYMBOL ROOTS:
  {
    const symbolNodesIndex = indexJsonDomNodeArrayById(symbolNodes);

    // Each root node is a top level component in our Component.ComponentMap.
    // We assume that every top level component that comes from JSONNodes
    // is a user component.
    symbolRoots(symbolNodes).forEach((node) => {
      const component = reifyComponent(
        symbolNodesIndex,
        node,
        dataTypes,
        plugins
      );

      componentMap.set_mut(
        qualify(sitePluginName, Component.name(node._id)),
        component
      );
    });
  }

  // PAGE ROOT:
  reifyPageRoot(pageNodes, dataTypes, plugins, componentMap);

  return new QualifiedMap(componentMap._data);
};

// Reifies only the page nodes, deriving dataTypes from an existing ComponentMap
// instead of from symbol JSON nodes. Used during page switches where
// the server omits symbols due to no symbol changes, and we reuse the components already in memory.
export const reifyPageNodesToComponentMap = (
  rawPageNodes: Array<JSONNode>,
  existingComponentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType
): Component.ComponentMap => {
  const dataTypes = dataTypesFromComponents(plugins, existingComponentMap);
  const pageNodes = migrateFlattenOverrides(plugins, dataTypes, rawPageNodes);
  const componentMap = new MutableQualifiedMap<
    Component.ComponentName,
    Component.ComponentType
  >();
  reifyPageRoot(pageNodes, dataTypes, plugins, componentMap);
  return new QualifiedMap(componentMap._data);
};

const reifyPageRoot = (
  pageNodes: Array<JSONNode>,
  dataTypes: Plugins.DataTypes,
  plugins: Plugins.PluginsType,
  componentMap: MutableQualifiedMap<
    Component.ComponentName,
    Component.ComponentType
  >
): void => {
  const pageNodesIndex = indexJsonDomNodeArrayById(pageNodes);
  const pageRootNodes = rootIds(plugins, dataTypes)(pageNodes);

  // TEMPORARY: We only expect a single page root, but we take the last one in
  // the list in the case there happen to be site data examples where more than
  // one page is being reified at once.
  //
  // This accomplishes two things:
  //
  // 1. Reports any unexpected cases where we encounter multiple page roots
  // 2. Keeps the previous behavior (override the `page` component with the last
  //    root in the list), at least for now.
  //
  // This will be revisited after these checks have had time to accumulate error
  // reports.
  if (pageRootNodes.length !== 1) {
    // Please see https://github.com/webflow/webflow/pull/39900
    // eslint-disable-next-line webflow/better-bug-reporting
    reportBug(
      'ComponentMapReificationError',
      'Unexpected number of page roots encountered; expected just one',
      {
        pageRootNodes,
      },
      {skipConsole: true}
    );
  }

  pageRootNodes.forEach((id) => {
    const node = pageNodesIndex[id];
    if (node === undefined) return;

    // This is a bug in localization; localization is being refactored to avoid
    // this problem. See:
    // https://webflow.slack.com/archives/C07J66RVAGH/p1778020246278449
    if (node.tag !== 'body' && node.type !== 'Body') {
      return;
    }

    const component = reifyComponent(pageNodesIndex, node, dataTypes, plugins);
    componentMap.set_mut(
      qualify(Plugins.name('__SitePlugin'), Component.name('page')),
      Component.update(component, {
        displayName: 'Page',
        icon: pageIcon(),
      })
    );
  });
};

const reifyComponent = (
  index: JSONNodeIndex,
  jsonNode: JSONNode,
  dataTypes: Plugins.DataTypes,
  plugins: Plugins.PluginsType
): Component.ComponentType => {
  const element = reifyElement({
    jsonNode,
    dataTypes,
    plugins,
    index,
  });
  const sym = isSymbolRoot(jsonNode) ? jsonNode.data?.sym : undefined;
  const displayName = sym?.name;
  const description = sym?.description ?? '';
  const groupName = sym?.groupName ?? '';
  const origin = sym?.origin || undefined;
  const variants = sym?.variants;
  const source = sym?.source;
  // Prune stale dismissed element IDs that no longer exist in the node index
  const dismissedPropElementIds = pruneDismissedPropElementIds(
    sym?.dismissedPropElementIds as string[] | undefined,
    index
  );
  const placeholderBaseVariantName = sym?.placeholderBaseVariantName;
  const hiddenForNonComponentManagers = sym?.hiddenForNonComponentManagers;
  const defaultData =
    sym?.defaultData !== undefined
      ? parseDefaultData(sym.defaultData)
      : undefined;

  const dataType = dataTypes.get(
    qualify(Plugins.name('__SitePlugin'), Component.name(jsonNode._id))
  );

  return Component.reify({
    render: element,
    dataType: dataType
      ? migrateRemoveTopLevelLayoutNone(dataType)
      : TEmptyRecord,
    displayName,
    description,
    groupName,
    icon: componentIcon(),
    origin,
    variants,
    source,
    dismissedPropElementIds,
    placeholderBaseVariantName,
    hiddenForNonComponentManagers,
    defaultData,
  });
};

const accDataType = (
  acc: MutableQualifiedMap<Component.ComponentName, Type>,
  component: Component.ComponentType,
  name: Qualified<Component.ComponentName>
) => {
  acc.set_mut(name, Component.getDataType(component));
  return acc;
};

// Memoized on (plugins, componentMap) identity via nested weakMemo. The result
// is a pure function of its inputs, so this is safe: an edit produces a new
// componentMap reference (Immutable structural sharing) and recomputes, while
// unchanged maps reuse the cached DataTypes. Without this, every per-render
// reification call (notably rich-text via reifyElementTree) re-reduced the
// entire site's componentMap — an O(whole-map) cost per call. (DSYS-4715)
const dataTypesFromComponentsMemo = weakMemo((plugins: Plugins.PluginsType) =>
  weakMemo((componentMap: Component.ComponentMap): Plugins.DataTypes => {
    const pluginDataTypes = Plugins.getPluginDataTypes(plugins);
    // Default values provide an empty ComponentMap for Reification under migration, this
    // optimization helps us avoid a scaling issue: https://github.com/webflow/webflow/pull/70229
    if (componentMap.size() === 0) return pluginDataTypes;
    return componentMap
      .reduce(accDataType, pluginDataTypes.asMutableCopy())
      .asNonMutableCopy();
  })
);

export const dataTypesFromComponents = (
  plugins: Plugins.PluginsType,
  componentMap: Component.ComponentMap
): Plugins.DataTypes => dataTypesFromComponentsMemo(plugins)(componentMap);

export const reifyElementSubtree = (
  rawJsonNodes: Array<JSONNode>,
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType
) => {
  if (!rawJsonNodes.length) {
    throw new Error('Subtree reification requires an array of JSON nodes.');
  }
  const dataTypes = dataTypesFromComponents(plugins, componentMap);
  const jsonNodes = migrateFlattenOverrides(plugins, dataTypes, rawJsonNodes);
  const newTreeElements = reifyToExpressions(jsonNodes, dataTypes, plugins);

  if (newTreeElements.length > 1) {
    throw new Error('Subtree reification resulted in more than one root!');
  }

  const expr = newTreeElements[0];
  if (!expr) {
    throw new Error('Subtree reification resulted in no elements!');
  }
  const element = Expressions.getElement(expr);

  if (!element) {
    throw new Error('Subtree reification did not result in an Element!');
  }

  return element;
};

export const reifyToExpressions = (
  jsonNodes: Array<JSONNode>,
  dataTypes: Plugins.DataTypes,
  plugins: Plugins.PluginsType
): Array<Expression> => {
  const index = indexJsonDomNodeArrayById(jsonNodes);
  return rootIds(
    plugins,
    dataTypes
  )(jsonNodes).map(build(plugins)(index)(dataTypes));
};

// A function that returns back the provided jsonNodes as Elements, differs from
// reifyElementSubtree by being able to return multiple Elements at the root
// level. Can for example return [h1, paragraph, image] (as Elements)
export const reifyElementTree = (
  jsonNodes: Array<JSONNode>,
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType
) => {
  const dataTypes = dataTypesFromComponents(plugins, componentMap);
  const exprs = reifyToExpressions(jsonNodes, dataTypes, plugins);
  return flatMap<Expression, ExpressionElement>((expr) => {
    const element = Expressions.getElement(expr);
    return element ? [element] : emptyArray;
  })(exprs);
};

const build =
  (plugins: Plugins.PluginsType) =>
  (index: JSONNodeIndex) =>
  (dataTypes: Plugins.DataTypes) =>
  (id: string) => {
    const node = index[id] as JSONNode;
    return reifyElement({jsonNode: node, dataTypes, plugins, index});
  };

export const indexJsonDomNodeArrayById = weakMemo(
  (items: Array<JSONNode>): JSONNodeIndex => {
    const index: Record<string, any> = {};
    const l = items.length;

    for (let i = 0; i < l; i++) {
      const item = items[i];
      if (item) index[item._id] = item;
    }

    return index;
  }
);

const getRecord = matchExpression<
  (arg1: Record<string, Expression>) => Record<string, Expression>
>({
  Record: constant,
  _: constantIdentity,
});

const getOverrides = matchExpression<
  (arg1: Record<string, Expression>) => Record<string, Expression>
>({
  Record: (sym) => (val: Record<string, Expression>) =>
    sym.overrides ? getRecord(sym.overrides)(val) : val,
  _: constantIdentity,
});

const getSymOverrides = matchExpression<Record<string, Expression>>({
  Record: (val) => (val.sym ? getOverrides(val.sym)(val) : val),
  _: (_) => emptyObject,
});
